import React, { ChangeEvent, FC, useState, useEffect, SyntheticEvent } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { toInteger } from 'lodash';

import { Typography } from '@material-ui/core';

import { updateAiringMutation } from './apiMutations';
import { getAiring } from './apiQueries';
import AiringDetails from './AiringDetails';
import AiringDetailsEditor from './AiringDetailsEditor';

import EditorForm from '../common/EditorForm';
import EditorialHighlights from '../common/EditorialHighlights';

import Exclusions from '../exclusions/Exclusions';

import { useAlertContext } from '../../context/alertContext';
import { convertAiringToAiringInput } from '../../utils/inputTransform';
import { Airing as AiringType, AiringClientExclusions, Status } from '../../utils/types';

export const Airing: FC<AiringProps> = (props) => {
	const { id }: AiringParams = useParams();

	const { edit } = props;

	const history = useHistory();

	const { setAlert } = useAlertContext();

	const [updatedOffset, setUpdatedOffset] = useState<string>('');
	const [updatedStatus, setUpdatedStatus] = useState<Status | null>(null);
	const [clientExclusions, setClientExclusions] = useState<AiringClientExclusions>([]);
	const [updatedProgramSynopsis, setUpdatedProgramSynopsis] = useState<string>('');
	const [updatedProgramStartTime, setUpdatedProgramStartTime] = useState<string>('');
	const [updatedProgramEndTime, setUpdatedProgramEndTime] = useState<string>('');
	const [saveDisabled, setSaveDisabled] = useState(true);

	const { loading, error, data, refetch } = useQuery<AiringQueryResponse, AiringParams>(getAiring, {
		variables: { id: decodeURIComponent(id) },
		fetchPolicy: 'no-cache'
	});

	const [updateAiring] = useMutation(updateAiringMutation);

	useEffect(() => {
		const offsets = data?.getAiring?.offsets;
		setUpdatedOffset(offsets && offsets?.items?.length ? offsets.items[0].offset.toString() : '');

		const status = data?.getAiring?.status;
		setUpdatedStatus(status as Status);

		const programSynopsis = data?.getAiring?.programSynopsis;
		setUpdatedProgramSynopsis(programSynopsis || '');

		const programStartTime = data?.getAiring?.programStartTime;
		setUpdatedProgramStartTime(programStartTime || '');

		const programEndTime = data?.getAiring?.programEndTime;
		setUpdatedProgramEndTime(programEndTime || '');

		const dataClientExclusions = data?.getAiring?.clientExclusions;
		if (dataClientExclusions) setClientExclusions(dataClientExclusions);
	}, [data]);

	if (loading) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error loading data from the server</Typography>;
	if (!data?.getAiring) return <Typography>No airing found</Typography>;

	const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
		const { name, value } = event.target;
		switch (name) {
			case 'offset':
				setUpdatedOffset(value);
				break;
			case 'programSynopsis':
				setUpdatedProgramSynopsis(value);
				break;
			case 'programStartTime':
				setUpdatedProgramStartTime(value);
				break;
			case 'programEndTime':
				setUpdatedProgramEndTime(value);
				break;
		}
		setSaveDisabled(false);
	};

	const handleChangeMulti = (event: ChangeEvent<{ value: unknown }>): void => {
		const { name, value } = event.target as HTMLSelectElement;
		switch (name) {
			case 'clientExclusions':
				setClientExclusions(value as unknown as AiringClientExclusions);
				break;
			case 'status':
				setUpdatedStatus(value as Status);
				break;
		}
		setSaveDisabled(false);
	};

	const handleAiringFormCancel = (event: SyntheticEvent): void => {
		history.push(`/airings/${id}`);
	};

	const handleAiringFormSubmit = async (event: SyntheticEvent): Promise<unknown> => {
		event.stopPropagation();
		event.preventDefault();

		const airing: AiringType = {
			id,
			channelId,
			startTime,
			businessLogic,
			status,
			clientExclusions,
			programSynopsis: updatedProgramSynopsis,
			programStartTime: updatedProgramStartTime,
			programEndTime: updatedProgramEndTime
		};

		if (updatedOffset) {
			airing.offsets = {
				items: [
					{
						segment: 0,
						offset: toInteger(updatedOffset)
					}
				]
			};
		}

		if (updatedStatus) {
			airing.status = updatedStatus;
		}

		const airingInput = convertAiringToAiringInput(airing);

		if (window.confirm('Are you sure you want to update the airing? This cannot be undone.')) {
			setSaveDisabled(true);
			try {
				if (!game?.id) {
					throw new Error('Game Id missing! Cannot update Airing!');
				}
				await updateAiring({
					variables: { airing: airingInput, gameId: game.id }
				});
				setAlert('Airing updated');
				history.push(`/airings/${id}`);
			} catch (e) {
				setAlert(`Could not update airing. ${e}`);
				setSaveDisabled(false);
			}
		}
		return true;
	};

	const {
		alternateIds,
		businessLogic,
		channel,
		channelId,
		drm,
		editorialHighlight,
		game,
		offsets,
		programEndTime,
		programStartTime,
		programSynopsis,
		sources,
		startTime,
		status
	} = data?.getAiring;

	const getView = (edit?: string): JSX.Element => {
		switch (edit) {
			case 'edit':
				return (
					<EditorForm
						cancelText='Cancel'
						onCancel={handleAiringFormCancel}
						onSubmit={handleAiringFormSubmit}
						submitText='Update Airing'
						saveDisabled={saveDisabled}>
						<AiringDetailsEditor
							id={id}
							channelId={channelId}
							status={status}
							offset={updatedOffset}
							clientExclusions={clientExclusions}
							programSynopsis={updatedProgramSynopsis}
							programStartTime={updatedProgramStartTime}
							programEndTime={updatedProgramEndTime}
							onChange={handleChange}
							onChangeMulti={handleChangeMulti}
						/>
					</EditorForm>
				);
			case 'editorialHighlight':
				return (
					<EditorialHighlights
						highlightType='airing'
						airings={[
							{
								id: id,
								channelId: channelId,
								startTime: startTime,
								businessLogic: businessLogic,
								channel: channel,
								editorialHighlight: editorialHighlight,
								game: game,
								status: status,
								offsets: offsets
							}
						]}
						game={game || { id: '' }}
						onAlert={setAlert}
						editorialHighlight={editorialHighlight || null}
					/>
				);

			case 'exclusions':
				return <Exclusions channel={channel} drm={drm} game={game} id={id} pageType={'airing'} sources={sources} status={status} />;
			default:
				return (
					<AiringDetails
						alternateIds={alternateIds}
						businessLogic={businessLogic}
						channel={channel}
						channelId={channelId}
						clientExclusions={clientExclusions}
						drm={drm}
						editorialHighlight={editorialHighlight}
						game={game}
						id={id}
						offsets={offsets}
						programEndTime={programEndTime}
						programSynopsis={programSynopsis}
						programStartTime={programStartTime}
						sources={sources}
						refetchAiringDetails={refetch}
						startTime={startTime}
						status={status}
					/>
				);
		}
	};

	return getView(edit);
};

type AiringParams = { id: string };

type AiringProps = { edit?: string };

type AiringQueryResponse = {
	getAiring: AiringType;
};

export default Airing;
